export const unwrapResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return response;
  }
  
  // Check for common wrapper keys (case-insensitive priority)
  if (response.Data !== undefined) return response.Data;
  if (response.data !== undefined) return response.data;
  if (response.response !== undefined) return response.response;
  if (response.result !== undefined) return response.result;
  if (response.payload !== undefined) return response.payload;
  
  // No wrapper found, return as-is
  return response;
};

export const extractArray = (response, possibleKeys = []) => {
  const unwrapped = unwrapResponse(response);
  
  // If unwrapped is already an array
  if (Array.isArray(unwrapped)) {
    return unwrapped;
  }
  
  // If unwrapped is not an object
  if (!unwrapped || typeof unwrapped !== 'object') {
    return [];
  }
  
  // Try each possible key
  for (const key of possibleKeys) {
    if (Array.isArray(unwrapped[key])) {
      return unwrapped[key];
    }
    
    // Try case-insensitive match
    const lowerKey = key.toLowerCase();
    const matchingKey = Object.keys(unwrapped).find(
      k => k.toLowerCase() === lowerKey
    );
    
    if (matchingKey && Array.isArray(unwrapped[matchingKey])) {
      return unwrapped[matchingKey];
    }
  }
  
  return [];
};

export const extractCount = (response, fallback = 0) => {
  const unwrapped = unwrapResponse(response);
  
  if (!unwrapped || typeof unwrapped !== 'object') {
    return fallback;
  }
  
  // Try common count field names
  const countFields = [
    'total', 'Total', 'count', 'Count',
    'totalCount', 'TotalCount', 'total_count',
    'totalRecords', 'TotalRecords', 'total_records',
  ];
  
  for (const field of countFields) {
    if (typeof unwrapped[field] === 'number') {
      return unwrapped[field];
    }
  }
  
  return fallback;
};

export const isSuccessResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return false;
  }
  
  // Check explicit success indicators
  if (response.success === true || response.Success === true) return true;
  if (response.status === 'success' || response.Status === 'success') return true;
  if (response.ok === true || response.Ok === true) return true;
  
  // Check HTTP status codes
  if (response.status >= 200 && response.status < 300) return true;
  if (response.statusCode >= 200 && response.statusCode < 300) return true;
  
  // If no explicit indicators, assume success if data exists
  return response.Data !== undefined || response.data !== undefined;
};

export const extractError = (response) => {
  if (!response || typeof response !== 'object') {
    return null;
  }
  
  // Check common error fields
  if (response.error) return String(response.error);
  if (response.Error) return String(response.Error);
  if (response.message) return String(response.message);
  if (response.Message) return String(response.Message);
  if (response.errorMessage) return String(response.errorMessage);
  if (response.ErrorMessage) return String(response.ErrorMessage);
  
  return null;
};

export const wrapResponse = (data, meta = {}) => {
  return {
    data,
    success: true,
    ...meta,
  };
};

export default {
  unwrapResponse,
  extractArray,
  extractCount,
  isSuccessResponse,
  extractError,
  wrapResponse,
};
